import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Box,
  Chip,
  Dialog,
  InputAdornment,
  InputBase,
  Stack,
  Typography,
} from '@mui/material'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import NorthWestRoundedIcon from '@mui/icons-material/NorthWestRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi'
import EventIcon from '@mui/icons-material/Event'
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled'
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded'
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { useNavigate } from 'react-router-dom'
import { UserContext } from '../../../App'
import { SiteModeContext } from '../MainFrame'
import API from '../../../services/API'
import { REGISTRY } from '../../../navigation/registry'
import { getSectionTitle } from '../../../navigation/sections'
import { getEntryAccess } from '../../../navigation/visibility'
import { useNavContext } from '../../../navigation/useNavigation'
import { getVisibleActions } from '../../../navigation/actions'
import { getNavIcon } from '../../../navigation/icons'
import { getRecentPages } from '../../../navigation/recents'
import { getDocsIndex } from '../../../docs/components/docsSearchIndex'
import { runDocsMappingCheck } from '../../../navigation/devChecks'

// ⌘K search. Groups, in order: Pages and Actions (local, from the navigation
// registry), Docs (local MiniSearch over the knowledgebase), then live
// entities from GET /global-search (people, vehicles, events, PTS - each only
// for users allowed to see them, enforced server-side). An empty query shows
// recent pages and favourites instead.

const rankPages = (pages, query) => {
  const q = query.trim().toLowerCase()
  if (!q) return pages

  return pages
    .map((page) => {
      const title = page.title.toLowerCase()
      const haystack =
        `${title} ${page.keywords ?? ''} ${page.description ?? ''} ${
          page.section ?? ''
        }`.toLowerCase()

      let score = -1
      if (title.startsWith(q)) score = 3
      else if (title.includes(q)) score = 2
      else if (q.split(/\s+/).every((word) => haystack.includes(word))) score = 1

      return { page, score }
    })
    .filter((x) => x.score >= 0)
    .sort((a, b) => b.score - a.score)
    .map((x) => x.page)
}

const GROUP_META = {
  pages: { label: 'Pages' },
  actions: { label: 'Create' },
  docs: { label: 'Knowledgebase' },
  people: { label: 'People', icon: <PersonRoundedIcon /> },
  vehicles: { label: 'Vehicles', icon: <LocalTaxiIcon /> },
  events: { label: 'Events', icon: <EventIcon /> },
  pts: { label: 'Patient Transport', icon: <DirectionsCarFilledIcon /> },
  recent: { label: 'Recent' },
  favourites: { label: 'Favourites' },
}

const GroupHeader = ({ type }) => (
  <Typography
    variant="overline"
    sx={{
      display: 'block',
      px: 1.5,
      pt: 1,
      pb: 0.25,
      fontWeight: 800,
      fontSize: '0.6rem',
      letterSpacing: 0.8,
      color: 'text.secondary',
    }}
  >
    {GROUP_META[type]?.label ?? type}
  </Typography>
)

const ResultRow = ({ row, active, onClick, onHover }) => (
  <Box
    data-active={active ? 'true' : undefined}
    onClick={onClick}
    onMouseEnter={onHover}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      px: 1.5,
      py: 1,
      borderRadius: 2,
      cursor: 'pointer',
      backgroundColor: active ? 'anMuted.main' : 'transparent',
    }}
  >
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        backgroundColor: 'rgba(35,154,164,0.12)',
        color: '#1E7F88',
        '& svg': { fontSize: 18 },
      }}
    >
      {row.icon ?? (
        <NorthWestRoundedIcon
          sx={{ fontSize: 16, transform: 'rotate(90deg)' }}
        />
      )}
    </Box>
    <Box minWidth={0} flex={1}>
      <Typography fontWeight={700} fontSize="0.9rem" noWrap>
        {row.title}
      </Typography>
      {row.subtitle && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.subtitle}
        </Typography>
      )}
    </Box>
    {row.chip && (
      <Chip
        label={row.chip}
        size="small"
        sx={{
          borderRadius: 1,
          fontSize: '0.65rem',
          height: 20,
          fontWeight: 700,
          color: 'text.secondary',
          backgroundColor: 'anMuted.main',
          display: { xs: 'none', sm: 'inline-flex' },
        }}
      />
    )}
  </Box>
)

export const GlobalSearchDialog = () => {
  const { searchOpen, setSearchOpen } = useContext(SiteModeContext)
  const { user } = useContext(UserContext)
  const ctx = useNavContext()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [serverGroups, setServerGroups] = useState([])
  const [ptsLocked, setPtsLocked] = useState(false)
  const [searching, setSearching] = useState(false)
  const listRef = useRef(null)
  const abortRef = useRef(null)
  const debounceRef = useRef(null)

  const allPages = useMemo(() => {
    if (process.env.NODE_ENV !== 'production') runDocsMappingCheck()
    return REGISTRY.filter((entry) => getEntryAccess(entry, ctx) === 'visible')
  }, [ctx])

  const actions = useMemo(() => getVisibleActions(ctx), [ctx])

  useEffect(() => {
    if (searchOpen) {
      setQuery('')
      setActiveIndex(0)
      setServerGroups([])
      setPtsLocked(false)
    }
  }, [searchOpen])

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  // Debounced entity search against the server while the user types
  useEffect(() => {
    if (!searchOpen) return undefined
    clearTimeout(debounceRef.current)
    abortRef.current?.abort()

    const q = query.trim()
    if (q.length < 2) {
      setServerGroups([])
      setPtsLocked(false)
      setSearching(false)
      return undefined
    }

    setSearching(true)
    debounceRef.current = setTimeout(() => {
      const controller = new AbortController()
      abortRef.current = controller
      API.get('/global-search', {
        params: { q },
        signal: controller.signal,
        skipGlobalErrorHandler: true,
      })
        .then((res) => {
          if (controller.signal.aborted) return
          if (res.status === 200) {
            setServerGroups(res.data?.groups ?? [])
            setPtsLocked(!!res.data?.ptsLocked)
          }
          setSearching(false)
        })
        .catch(() => {
          if (!controller.signal.aborted) setSearching(false)
        })
    }, 250)

    return () => clearTimeout(debounceRef.current)
  }, [query, searchOpen])

  // Cmd/Ctrl+K opens search from anywhere
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [setSearchOpen])

  // One flat, ordered list of rows across every group - this is what the
  // keyboard navigates; headers are rendered between group boundaries.
  const rows = useMemo(() => {
    const q = query.trim()

    if (!q) {
      const recent = getRecentPages(user?._id)
        .slice(0, 5)
        .map((entry) => ({
          group: 'recent',
          key: `recent-${entry.id}`,
          title: entry.title,
          subtitle: entry.description,
          icon: <HistoryRoundedIcon />,
          url: entry.path,
        }))
      const favourites = (user?.uiPreferences?.favouritePages ?? []).map(
        (fav) => ({
          group: 'favourites',
          key: `fav-${fav.path}`,
          title: fav.label,
          subtitle: fav.path,
          icon: <StarRoundedIcon sx={{ color: '#CCA43B' }} />,
          url: fav.path,
        }),
      )
      return [...recent, ...favourites]
    }

    const pages = rankPages(
      allPages.map((entry) => ({
        entry,
        title: entry.title,
        keywords: entry.keywords,
        description: entry.description,
        section: getSectionTitle(entry.section),
      })),
      q,
    )
      .slice(0, 7)
      .map(({ entry, section }) => ({
        group: 'pages',
        key: `page-${entry.id}`,
        title: entry.title,
        subtitle: entry.description,
        chip: section,
        url: entry.path,
      }))

    const lowered = q.toLowerCase()
    const matchedActions = actions
      .filter((action) =>
        `${action.label} ${action.keywords ?? ''}`
          .toLowerCase()
          .includes(lowered),
      )
      .slice(0, 3)
      .map((action) => ({
        group: 'actions',
        key: action.id,
        title: action.label,
        subtitle: 'Create new',
        icon: <AddCircleOutlineRoundedIcon />,
        url: action.path,
      }))

    let docs = []
    try {
      docs = getDocsIndex()
        .search(q, { prefix: true, fuzzy: 0.2 })
        .slice(0, 4)
        .map((hit) => ({
          group: 'docs',
          key: `doc-${hit.id}`,
          title: hit.title,
          subtitle: hit.summary,
          chip: hit.section,
          icon: <MenuBookRoundedIcon />,
          url: `/docs/${hit.sectionID}/${hit.pageId}`,
        }))
    } catch {
      // docs index unavailable - skip the group
    }

    const entityRows = serverGroups.flatMap((group) =>
      group.items.map((item) => ({
        group: group.type,
        key: `${group.type}-${item.id}`,
        title: item.title,
        subtitle: item.subtitle,
        icon: GROUP_META[group.type]?.icon,
        url: item.webUrl,
      })),
    )

    return [...pages, ...matchedActions, ...docs, ...entityRows]
  }, [query, allPages, actions, serverGroups, user])

  const navigableRows = useMemo(() => rows.filter((row) => row.url), [rows])

  useEffect(() => {
    if (activeIndex > navigableRows.length - 1) {
      setActiveIndex(Math.max(navigableRows.length - 1, 0))
    }
  }, [navigableRows.length, activeIndex])

  const close = () => setSearchOpen(false)

  const goTo = (row) => {
    if (!row?.url) return
    close()
    navigate(row.url)
  }

  const onInputKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((prev) => Math.min(prev + 1, navigableRows.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((prev) => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter' && navigableRows[activeIndex]) {
      goTo(navigableRows[activeIndex])
    }
  }

  // Keep the active row scrolled into view during keyboard navigation
  useEffect(() => {
    const activeEl = listRef.current?.querySelector('[data-active="true"]')
    activeEl?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  // Render rows grouped, tracking the flat index for highlight/keyboard
  let flatIndex = -1
  let lastGroup = null

  return (
    <Dialog
      open={!!searchOpen}
      onClose={close}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          position: 'absolute',
          top: { xs: 12, sm: 64 },
          m: { xs: 1, sm: 2 },
          width: { xs: 'calc(100% - 16px)', sm: '100%' },
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <InputBase
          autoFocus
          fullWidth
          placeholder="Search pages, people, vehicles, events, docs..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onInputKeyDown}
          startAdornment={
            <InputAdornment position="start">
              <SearchRoundedIcon sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          }
          endAdornment={
            <InputAdornment position="end">
              <Chip
                label="esc"
                size="small"
                onClick={close}
                sx={{
                  borderRadius: 1,
                  fontSize: '0.65rem',
                  height: 20,
                  color: 'text.secondary',
                  backgroundColor: 'anMuted.main',
                }}
              />
            </InputAdornment>
          }
          sx={{ fontSize: '1rem' }}
        />
      </Box>

      <Box ref={listRef} sx={{ maxHeight: '55vh', overflowY: 'auto', p: 1 }}>
        {rows.length === 0 ? (
          <Box p={3} textAlign="center">
            {query.trim() ? (
              <>
                <Typography color="text.secondary">
                  {searching
                    ? 'Searching...'
                    : `Nothing matches "${query}"`}
                </Typography>
                {!searching && (
                  <Typography variant="caption" color="text.disabled">
                    Try a different word — for example a page name, a
                    colleague, a reg plate or an event
                  </Typography>
                )}
              </>
            ) : (
              <Typography color="text.secondary">
                Type to search — your recent pages and favourites will show
                here as you use AmbuNet
              </Typography>
            )}
          </Box>
        ) : (
          rows.map((row) => {
            const showHeader = row.group !== lastGroup
            lastGroup = row.group
            if (row.url) flatIndex += 1
            const rowIndex = flatIndex
            return (
              <React.Fragment key={row.key}>
                {showHeader && <GroupHeader type={row.group} />}
                <ResultRow
                  row={row}
                  active={row.url ? rowIndex === activeIndex : false}
                  onClick={() => goTo(row)}
                  onHover={() => row.url && setActiveIndex(rowIndex)}
                />
              </React.Fragment>
            )
          })
        )}
        {ptsLocked && query.trim().length >= 2 && (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ px: 1.5, py: 1, color: 'text.secondary' }}
          >
            <LockOutlinedIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption">
              PTS bookings are hidden — verify with MFA (open any PTS page) to
              search them
            </Typography>
          </Stack>
        )}
      </Box>
    </Dialog>
  )
}

// Desktop trigger - the pill in the centre of the top banner
export const GlobalSearchBar = () => {
  const { setSearchOpen } = useContext(SiteModeContext)
  const isMac =
    typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform)

  return (
    <Box
      onClick={() => setSearchOpen(true)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        maxWidth: 440,
        px: 1.5,
        py: 0.75,
        borderRadius: 999,
        cursor: 'pointer',
        backgroundColor: 'anMuted.main',
        border: '1px solid',
        borderColor: 'divider',
        color: 'text.secondary',
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        '&:hover': {
          backgroundColor: 'anBackground.main',
          borderColor: '#94A3B8',
        },
      }}
    >
      <SearchRoundedIcon sx={{ fontSize: 20 }} />
      <Typography fontSize="0.85rem" flex={1} noWrap>
        Search AmbuNet...
      </Typography>
      <Chip
        label={isMac ? '⌘K' : 'Ctrl K'}
        size="small"
        sx={{
          borderRadius: 1,
          fontSize: '0.65rem',
          height: 20,
          fontWeight: 700,
          color: 'text.secondary',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      />
    </Box>
  )
}

// Mobile trigger - compact bar at the top of the sidebar drawer
export const SidebarSearchButton = () => {
  const { setSearchOpen } = useContext(SiteModeContext)

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      onClick={() => setSearchOpen(true)}
      sx={{
        mx: 1.5,
        my: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: 999,
        cursor: 'pointer',
        color: 'rgba(255,255,255,0.75)',
        backgroundColor: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.14)',
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,0.14)',
        },
      }}
    >
      <SearchRoundedIcon sx={{ fontSize: 18 }} />
      <Typography fontSize="0.85rem">Search...</Typography>
    </Stack>
  )
}

export default GlobalSearchDialog
